import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { QrCode, RefreshCw, CheckCircle2, Clock, User } from "lucide-react";

/* Scan & Share — front-desk QR flow.
 * 1. Operator clicks "Start Scan" → backend creates a session token.
 * 2. UI shows QR with { hipId, token, context, callback } JSON payload.
 * 3. Patient scans with ABHA app → ABDM POSTs profile to our HIP webhook.
 * 4. UI polls the session every 2 s and reveals the profile card + "Open" btn.
 */
export default function ScanShare() {
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const pollRef = useRef(null);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/scan-share/session", { kiosk: "Reception" });
      setSession(data);
      toast.success("QR generated — ready to scan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create session");
    } finally { setBusy(false); }
  };

  const stop = () => { setSession(null); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const loadRecent = () => api.get("/abdm/scan-share/recent", { params: { limit: 10 } }).then((r) => setRecent(r.data));

  useEffect(() => {
    if (!session || session.status !== "waiting") { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/abdm/scan-share/session/${session.token}`);
        if (data.status === "received") {
          setSession(data);
          toast.success(`${data.profile?.name || "Patient"} scanned — ${data.patient_created ? "new" : "linked"} record`);
          if (pollRef.current) clearInterval(pollRef.current);
          loadRecent();
        }
      } catch { /* ignore transient errors */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [session]);

  useEffect(() => { loadRecent(); }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left — QR panel */}
      <div className="lg:col-span-2 bg-white border p-4 space-y-3" data-testid="scan-share-panel">
        <div className="flex items-center justify-between">
          <div className="label-tiny flex items-center gap-2"><QrCode className="w-4 h-4"/> Scan &amp; Share · Front-desk QR</div>
          {session && session.status === "waiting" && <Badge className="bg-amber-100 text-amber-800 rounded-sm"><Clock className="w-3 h-3 mr-1"/> Waiting for scan…</Badge>}
          {session?.status === "received" && <Badge className="bg-teal-100 text-teal-800 rounded-sm"><CheckCircle2 className="w-3 h-3 mr-1"/> Scan received</Badge>}
        </div>
        <p className="text-[11px] text-blue-800 bg-blue-50 border border-blue-200 p-2 rounded-sm">
          Uses NHA <code>patient-share/v3/on-share</code>. Patient scans this QR with the ABHA app; their profile lands here in a few seconds and is auto-registered.
        </p>

        {!session && (
          <div className="text-center py-12">
            <Button data-testid="scan-share-start" onClick={start} disabled={busy} className="bg-blue-700 hover:bg-blue-800 rounded-sm">
              <QrCode className="w-4 h-4 mr-1"/> Generate QR for next patient
            </Button>
          </div>
        )}

        {session && session.status === "waiting" && (
          <div className="flex flex-col items-center py-4 space-y-3">
            <div className="p-3 bg-white border-2 border-blue-700" data-testid="scan-share-qr">
              <QRCodeCanvas value={JSON.stringify(session.qr_payload)} size={256} includeMargin={true} level="M"/>
            </div>
            <div className="text-[11px] text-slate-500 font-mono">token · {session.token.slice(0, 12)}…</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={start} disabled={busy}><RefreshCw className="w-4 h-4 mr-1"/> New QR</Button>
              <Button variant="outline" onClick={stop}>Cancel</Button>
            </div>
          </div>
        )}

        {session?.status === "received" && session.profile && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 border p-3">
              <div><span className="text-slate-500">Name:</span> <b>{session.profile.name}</b></div>
              <div><span className="text-slate-500">ABHA Number:</span> <span className="font-mono">{session.profile.abhaNumber || "-"}</span></div>
              <div><span className="text-slate-500">ABHA Address:</span> <span className="font-mono">{session.profile.abhaAddress || "-"}</span></div>
              <div><span className="text-slate-500">Gender:</span> {session.profile.gender || "-"}</div>
              <div><span className="text-slate-500">Mobile:</span> {session.profile.phoneNumber || "-"}</div>
              <div><span className="text-slate-500">Year of Birth:</span> {session.profile.yearOfBirth || "-"}</div>
            </div>
            <div className="flex gap-2">
              <Button className="bg-teal-700 hover:bg-teal-800 rounded-sm" onClick={() => window.location.href = `/patients/${session.patient_id}`}>
                <User className="w-4 h-4 mr-1"/> Open Patient
              </Button>
              <Button variant="outline" onClick={start}>Next patient</Button>
            </div>
          </div>
        )}
      </div>

      {/* Right — recent scans */}
      <div className="bg-white border">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="label-tiny">Recent scans</div>
          <button onClick={loadRecent} className="text-[10px] text-blue-700"><RefreshCw className="w-3 h-3 inline"/></button>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {recent.filter((r) => r.status !== "expired").length === 0 && <div className="p-6 text-center text-xs text-slate-500">No scans yet.</div>}
          {recent.filter((r) => r.status !== "expired").map((r) => (
            <div key={r.token} className="p-3 border-b text-xs">
              <div className="flex items-center justify-between">
                <b>{r.profile?.name || <span className="text-slate-400 italic">Waiting…</span>}</b>
                <Badge className={`rounded-sm ${r.status === "received" ? "bg-teal-100 text-teal-800" : "bg-amber-100 text-amber-800"}`}>{r.status}</Badge>
              </div>
              {r.profile?.abhaNumber && <div className="font-mono text-[10px]">{r.profile.abhaNumber}</div>}
              <div className="text-[10px] text-slate-500">{new Date(r.created_at).toLocaleString("en-IN")}</div>
              {r.patient_id && r.status === "received" && (
                <button onClick={() => window.location.href = `/patients/${r.patient_id}`}
                  className="text-[10px] text-blue-700 underline mt-1">Open patient →</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
